import type {ChangeEvent, ReactElement} from 'react';
import {useCallback, useRef, useState} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {ControlledListItemType2} from '@Components/controlled-list/components/controlled-list-item-type-2';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {Text, TextSize} from '@Components/text';
import {assertNever} from '@Utils/assert-never.util';
import {QR_LOGO_ACCEPTED_FILE_TYPES} from '@Components/qr-code-generator/qr-code-generator.types';
import {getQRLogoRejection} from '@Components/qr-code-generator/qr-code-generator.library';
import {blobToDataUrl} from '@Utils/file.util';
import {useDismissOnOutsideInteraction, useIsCompactQRTool, useIsIconOnlyQRActions} from '@Components/qr-code-generator/qr-code-generator-hooks';
import {QRActionSheet} from '@Components/qr-code-generator/components/qr-action-sheet/qr-action-sheet';
import styles from './qr-customize-menu.module.scss';
import type {QRCustomizeMenuProps, QRCustomizeMenuRow} from './qr-customize-menu.types';
import {QR_CUSTOMIZE_ADD_LOGO_ROW, QR_CUSTOMIZE_REMOVE_LOGO_ROW, QR_CUSTOMIZE_STYLE_ROW, QRCustomizeAction} from './qr-customize-menu.types';

export function QRCustomizeMenu({logo, onLogoChange, onStyleOpen}: QRCustomizeMenuProps): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [rejectionText, setRejectionText] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isCompact = useIsCompactQRTool();
  const isIconOnly = useIsIconOnlyQRActions();

  const closeMenu = useCallback((): void => {
    setIsMenuOpen(false);
  }, []);
  const menuAnchorRef = useDismissOnOutsideInteraction(isMenuOpen && !isCompact, closeMenu);

  const onLogoFileSelected = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const rejection = getQRLogoRejection(file);
    setRejectionText(rejection);
    if (rejection !== null) {
      return;
    }

    void blobToDataUrl(file)
      .then((dataUrl): void => {
        onLogoChange({file, dataUrl});
      })
      .catch((error: unknown): void => {
        window.PMW.log('The QR logo could not be read', error);
        setRejectionText(window.i18next.t('pmwjs_qr_generator_logo_unreadable'));
      });
  };

  const getMenuRows = (): QRCustomizeMenuRow[] => {
    return [QR_CUSTOMIZE_STYLE_ROW, logo === null ? QR_CUSTOMIZE_ADD_LOGO_ROW : QR_CUSTOMIZE_REMOVE_LOGO_ROW];
  };

  const onActionSelect = (action: QRCustomizeAction): void => {
    closeMenu();
    switch (action) {
      case QRCustomizeAction.STYLE:
        onStyleOpen();
        return;
      case QRCustomizeAction.LOGO:
        if (logo !== null) {
          onLogoChange(null);
          return;
        }
        fileInputRef.current?.click();
        return;
      default:
        assertNever(action);
    }
  };

  const onRowSelect = (rowId?: string): void => {
    const pickedRow = getMenuRows().find((row): boolean => {
      return row.action === rowId;
    });
    if (pickedRow === undefined) {
      return;
    }
    onActionSelect(pickedRow.action);
  };

  const isRowActive = (row: QRCustomizeMenuRow): boolean => {
    return row.action === QRCustomizeAction.LOGO && logo !== null;
  };

  const renderMenuRows = (): ReactElement[] => {
    return getMenuRows().map((row): ReactElement => {
      return (
        <ControlledListItemType2
          key={row.action}
          id={row.action}
          className={styles.qrCustomizeMenuRow}
          text={window.i18next.t(row.labelKey)}
          subText={window.i18next.t(row.descriptionKey)}
          thumbnail={{type: ThumbnailType.ICON_DEFAULT, icon: row.icon}}
          isSelected={isRowActive(row)}
          onClick={onRowSelect}
        />
      );
    });
  };

  const renderMenu = (): ReactElement | null => {
    if (!isMenuOpen) {
      return null;
    }
    if (isCompact) {
      return (
        <QRActionSheet label={window.i18next.t('pmwjs_qr_generator_style_action')} onClose={closeMenu}>
          <ul className={styles.qrCustomizeMenuList}>{renderMenuRows()}</ul>
        </QRActionSheet>
      );
    }
    return (
      <div className={styles.qrCustomizeMenuPanel} role="group" aria-label={window.i18next.t('pmwjs_qr_generator_style_action')}>
        <ul className={styles.qrCustomizeMenuList}>{renderMenuRows()}</ul>
      </div>
    );
  };

  const renderRejection = (): ReactElement | null => {
    if (rejectionText === null) {
      return null;
    }
    return <Text val={rejectionText} size={TextSize.SM} className={styles.qrCustomizeRejection} />;
  };

  return (
    <div ref={menuAnchorRef} className={styles.qrCustomizeMenu}>
      <Button
        text={isIconOnly ? undefined : window.i18next.t('pmwjs_qr_generator_style_action')}
        type={ButtonType.SECONDARY}
        size={ButtonSize.MEDIUM}
        icon="icon-color-palette"
        isActive={isMenuOpen}
        attributes={{
          'aria-haspopup': 'true',
          'aria-expanded': String(isMenuOpen),
          'aria-label': window.i18next.t('pmwjs_qr_generator_style_action'),
        }}
        onClick={(): void => {
          setIsMenuOpen(!isMenuOpen);
        }}
      />
      {renderMenu()}
      <input
        ref={fileInputRef}
        className={styles.qrCustomizeFileInput}
        type="file"
        accept={QR_LOGO_ACCEPTED_FILE_TYPES}
        aria-label={window.i18next.t('pmwjs_qr_generator_add_logo')}
        onChange={onLogoFileSelected}
      />
      {renderRejection()}
    </div>
  );
}
